import type {ChatStatusItem, PageChangeOperation, PageChangeSetStatusItem} from '../chat-status';
import {isPageChangeSetStatusItem} from '../chat-status';

export type PageChangeSetLabels = Record<string, string>;

/** What a button on a card asks the server to do. Resolves to an error text to show, or null when it worked. */
export type PageChangeSetActions = {
    apply: (set: PageChangeSetStatusItem) => Promise<string | null>;
    skip: (set: PageChangeSetStatusItem, operationId: string) => Promise<string | null>;
    cancel: (set: PageChangeSetStatusItem) => Promise<string | null>;
    /** Reads the set again, which also lets the server notice an apply whose job was lost. */
    refresh: (set: PageChangeSetStatusItem) => Promise<void>;
};

/** How often a set being applied is read again, in case a push about it was missed or its job was lost. */
const APPLYING_REFRESH_MS = 30_000;

type Options = {
    labels: PageChangeSetLabels;
    actions: PageChangeSetActions;
    currentPageId: () => string;
    isRunning: () => boolean;
    close: string;
    /** Scrolls to a change on the page. False when the page does not show it. */
    reveal: (set: PageChangeSetStatusItem, operation: PageChangeOperation) => boolean;
    /** Called when the buttons become available or unavailable, so the page's buttons can follow. */
    onBusyChange: () => void;
};

/**
 * The control strips of change sets in the chat: how many changes are staged on which page, one line per change
 * that jumps to it on the page, where its content is previewed, and the buttons that apply, cancel or skip them.
 * A finished set stays as a record of what was applied until it is dismissed.
 *
 * Cards are drawn from the server's view of the set every time it changes. The buttons never change a card
 * themselves, the answer from the server does.
 */
export class ChangeSetCards {
    private signature = '';
    private readonly dismissed = new Set<string>();
    /**
     * Set id to the error the last button press on that card came back with, and the set version it was about.
     * It is announced once, when it first shows, not again each time progress redraws the card.
     */
    private readonly errors = new Map<string, {text: string; version: string; announced?: boolean}>();
    /** Set id to the version last announced, so only real changes are read out. */
    private readonly announced = new Map<string, string>();
    private readonly announcer: HTMLElement;
    private busy = false;
    private rendered = false;
    /** The control pressed last. Browsers drop focus from a button once it is disabled, so it is kept here. */
    private focusAfterAction: string | undefined;
    private statusItems: ChatStatusItem[] = [];
    private refreshTimer = 0;

    constructor(private readonly element: HTMLElement, private readonly options: Options) {
        this.element.className = 'howspace-ai-change-sets';
        this.element.hidden = true;
        // The cards are redrawn whole, so they are not a live region themselves. One sentence per change is.
        this.announcer = document.createElement('div');
        this.announcer.className = 'sr-only';
        this.announcer.setAttribute('aria-live', 'polite');
        this.element.after(this.announcer);
    }

    render(statusItems: ChatStatusItem[]): void {
        this.statusItems = statusItems;
        const pageId = this.options.currentPageId();
        // Changes waiting for approval are shown, and applied, only on the page they were staged for. Leaving the
        // page hides them, and coming back shows them again. An apply shows its progress on any page, and a finished
        // set stays as the record of what changed.
        const sets = statusItems
            .filter(isPageChangeSetStatusItem)
            .filter(set => !this.dismissed.has(set.id))
            .filter(set => set.status !== 'staged' || set.pageId === pageId);
        sets.forEach(set => {
            const error = this.errors.get(set.id);
            if (error && error.version !== versionOf(set)) {
                this.errors.delete(set.id);
            }
        });
        const signature = JSON.stringify([
            pageId,
            this.busy,
            [...this.errors],
            sets.map(set => [set.id, set.status, set.revision, set.pageTitle, set.operations]),
        ]);
        if (signature !== this.signature) {
            this.signature = signature;
            const focusKey = (document.activeElement as HTMLElement | null)?.dataset?.focusKey ?? this.focusAfterAction;
            this.element.replaceChildren(...sets.map(set => this.renderCard(set)));
            this.element.hidden = sets.length === 0;
            this.syncDisabled();
            this.restoreFocus(focusKey);
            this.announce(sets);
            if (!this.busy) {
                this.focusAfterAction = undefined;
            }
        }
        this.syncDisabled();
        this.syncRefresh();
    }

    refresh(): void {
        this.render(this.statusItems);
    }

    /**
     * Whether a button press is waiting for the server.
     */
    isBusy(): boolean {
        return this.busy;
    }

    /**
     * Skip pressed on the page. It runs like the card's own Skip, so the card shows its error, but focus stays on
     * the page rather than jumping to the chat.
     */
    skipFromPage(set: PageChangeSetStatusItem, operationId: string): void {
        this.run(set, null, () => this.options.actions.skip(set, operationId));
    }

    /**
     * Whether one of the chat's sets is being applied. Its outcome reaches only this chat.
     */
    isApplying(): boolean {
        return this.applyingSets().length > 0;
    }

    /**
     * New chat waits while changes are applied, because their outcome reaches only this chat. The cards say so.
     */
    explainNewChatWaits(): void {
        const sets = this.applyingSets().filter(set => !this.dismissed.has(set.id));
        sets.forEach(set => this.errors.set(set.id, {text: this.label('newChatWaits'), version: versionOf(set)}));
        this.signature = '';
        this.render(this.statusItems);
    }

    /**
     * Applies run in the background and report through pushes. While one runs, its set is also read now and then,
     * also when the user is on another page, so a missed push or a lost job does not leave it applying for good.
     */
    private syncRefresh(): void {
        if (!this.isApplying()) {
            window.clearInterval(this.refreshTimer);
            this.refreshTimer = 0;
            return;
        }
        if (this.refreshTimer !== 0) {
            return;
        }
        this.refreshTimer = window.setInterval(() => {
            if (!this.element.isConnected) {
                window.clearInterval(this.refreshTimer);
                this.refreshTimer = 0;
                return;
            }
            this.applyingSets().forEach(set => void this.options.actions.refresh(set));
        }, APPLYING_REFRESH_MS);
    }

    private applyingSets(): PageChangeSetStatusItem[] {
        return this.statusItems.filter(isPageChangeSetStatusItem).filter(set => set.status === 'applying');
    }

    /**
     * Running replies come and go without the cards changing, so only the buttons follow them.
     */
    private syncDisabled(): void {
        const disabled = this.busy || this.options.isRunning();
        this.element.querySelectorAll<HTMLButtonElement>('button[data-action]').forEach(button => {
            button.disabled = disabled;
        });
    }

    /**
     * A redraw replaces the focused button. Focus goes to the same control when it is still there, otherwise to
     * the first control of the same card, or its heading.
     */
    private restoreFocus(focusKey: string | undefined): void {
        if (!focusKey) {
            return;
        }
        const same = this.element.querySelector<HTMLElement>(`[data-focus-key="${CSS.escape(focusKey)}"]`);
        if (same) {
            same.focus();
            return;
        }
        const setId = focusKey.split(':')[1] || '';
        const card = this.element.querySelector<HTMLElement>(`[data-set-id="${CSS.escape(setId)}"]`);
        const fallback = card?.querySelector<HTMLElement>('button[data-action]:not(:disabled)')
            ?? card?.querySelector<HTMLElement>('.howspace-ai-change-set-heading');
        fallback?.focus();
    }

    private announce(sets: PageChangeSetStatusItem[]): void {
        const changed = sets.filter(set => this.announced.get(set.id) !== versionOf(set));
        sets.forEach(set => this.announced.set(set.id, versionOf(set)));
        if (this.rendered && changed.length > 0) {
            this.announcer.textContent = changed.map(set => this.headingText(set)).join('. ');
        }
        this.rendered = true;
    }

    private renderCard(set: PageChangeSetStatusItem): HTMLElement {
        const card = document.createElement('section');
        card.className = `howspace-ai-change-set is-${set.status}`;
        card.dataset.setId = set.id;
        const heading = document.createElement('h3');
        heading.className = 'howspace-ai-change-set-heading';
        heading.id = `howspace-ai-change-set-${set.id}`;
        heading.tabIndex = -1;
        // Progress redraws the card while focus may rest here.
        heading.dataset.focusKey = `heading:${set.id}`;
        heading.textContent = this.headingText(set);
        card.setAttribute('aria-labelledby', heading.id);

        const headingRow = document.createElement('div');
        headingRow.className = 'howspace-ai-change-set-heading-row';
        headingRow.append(heading);
        if (set.status !== 'staged' && set.status !== 'applying') {
            headingRow.append(this.renderDismissButton(set));
        }
        card.append(headingRow);

        const list = document.createElement('ul');
        list.className = 'howspace-ai-change-set-list';
        set.operations
            .filter(() => set.status !== 'canceled')
            .filter(operation => set.status !== 'staged' || operation.status === 'pending')
            // A later edit of the same widget took its place and carries what it changed.
            .filter(operation => operation.reason !== 'replaced')
            .forEach(operation => list.append(this.renderOperation(set, operation)));
        if (list.childElementCount > 0) {
            card.append(list);
        }

        const error = this.errors.get(set.id);
        if (error) {
            const notice = document.createElement('p');
            notice.className = 'howspace-ai-change-set-error';
            if (!error.announced) {
                notice.setAttribute('role', 'alert');
                error.announced = true;
            }
            notice.textContent = error.text;
            card.append(notice);
        }

        if (set.status === 'staged') {
            card.append(this.renderActions(set));
        }

        return card;
    }

    private renderOperation(set: PageChangeSetStatusItem, operation: PageChangeOperation): HTMLElement {
        const item = document.createElement('li');
        item.className = `howspace-ai-change-set-item is-${operation.status}`;

        const icon = document.createElement('i');
        icon.className = `fa-light fa-${operation.icon || 'square'} howspace-ai-change-set-icon`;
        icon.setAttribute('aria-hidden', 'true');

        const body = document.createElement('div');
        body.className = 'howspace-ai-change-set-body';
        const title = operation.title || this.label('untitled');
        const lineText = this.label(operation.op === 'update_item' ? 'lineEdit' : 'lineAdd').replace('%title%', title);
        const canReveal = set.pageId === this.options.currentPageId()
            && (set.status === 'staged' || (operation.status === 'applied' && operation.widgetId !== null));
        if (canReveal) {
            const line = document.createElement('button');
            line.type = 'button';
            line.className = 'howspace-ai-change-set-line';
            line.textContent = lineText;
            line.setAttribute('aria-label', this.label('showOnPage').replace('%title%', lineText));
            line.dataset.focusKey = `line-${operation.id}:${set.id}`;
            line.addEventListener('click', () => {
                if (!this.options.reveal(set, operation)) {
                    this.errors.set(set.id, {text: this.label('notShown'), version: versionOf(set)});
                    this.render(this.statusItems);
                }
            });
            body.append(line);
        } else {
            const line = document.createElement('div');
            line.className = 'howspace-ai-change-set-title';
            line.textContent = lineText;
            body.append(line);
        }

        const outcome = this.outcomeText(operation);
        if (outcome) {
            const outcomeElement = document.createElement('p');
            outcomeElement.className = 'howspace-ai-change-set-outcome';
            outcomeElement.textContent = outcome;
            body.append(outcomeElement);
        }

        item.append(icon, body);
        if (set.status === 'staged' && operation.status === 'pending') {
            const skip = this.renderButton(this.label('skip'), 'howspace-ai-change-set-skip', () => (
                this.options.actions.skip(set, operation.id)
            ), set, `skip-${operation.id}`);
            skip.setAttribute('aria-label', this.label('skipNamed').replace('%title%', title));
            item.append(skip);
        }

        return item;
    }

    private renderActions(set: PageChangeSetStatusItem): HTMLElement {
        const actions = document.createElement('div');
        actions.className = 'howspace-ai-change-set-actions';
        const count = pendingCount(set);
        const applyLabel = count === 1
            ? this.label('applyOne')
            : this.label('apply').replace('%count%', String(count));
        actions.append(
            this.renderButton(applyLabel, 'howspace-ai-change-set-apply', () => (
                this.options.actions.apply(set)
            ), set, 'apply'),
            this.renderButton(this.label('cancel'), 'howspace-ai-change-set-cancel', () => (
                this.options.actions.cancel(set)
            ), set, 'cancel'),
        );

        return actions;
    }

    private renderButton(
        text: string,
        className: string,
        onClick: () => Promise<string | null>,
        set: PageChangeSetStatusItem,
        action: string,
    ): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.textContent = text;
        button.dataset.action = action;
        button.dataset.focusKey = `${action}:${set.id}`;
        // Every button asks the server to act on the set the card shows. While the agent is answering it may
        // still be adding to that set, so nothing is offered until it is done.
        button.disabled = this.busy || this.options.isRunning();
        button.addEventListener('click', () => this.run(set, action, onClick));

        return button;
    }

    /**
     * Runs a button's request. Every button waits for it, and the card is drawn again from the server's answer.
     */
    private run(set: PageChangeSetStatusItem, action: string | null, onClick: () => Promise<string | null>): void {
        if (this.busy || this.options.isRunning()) {
            return;
        }
        this.busy = true;
        this.focusAfterAction = action === null ? undefined : `${action}:${set.id}`;
        this.errors.delete(set.id);
        this.signature = '';
        this.element.querySelectorAll('button').forEach(element => {
            element.disabled = true;
        });
        this.options.onBusyChange();
        const version = versionOf(set);
        void onClick().then(error => {
            this.busy = false;
            if (error) {
                this.errors.set(set.id, {text: error, version: this.currentVersion(set.id) ?? version});
            }
            this.signature = '';
            this.render(this.statusItems);
            this.options.onBusyChange();
        });
    }

    private renderDismissButton(set: PageChangeSetStatusItem): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-change-set-dismiss';
        button.dataset.focusKey = `dismiss:${set.id}`;
        button.setAttribute('aria-label', this.options.close || 'Close');
        button.innerHTML = '<i class="fa-light fa-xmark" aria-hidden="true"></i>';
        button.addEventListener('click', () => {
            this.dismissed.add(set.id);
            this.errors.delete(set.id);
            button.closest('.howspace-ai-change-set')?.remove();
            this.element.hidden = this.element.childElementCount === 0;
        });

        return button;
    }

    private headingText(set: PageChangeSetStatusItem): string {
        const page = set.pageTitle || this.label('deletedPage');
        const count = pendingCount(set);
        const key = {
            staged: count === 1 ? 'headingOne' : 'heading',
            applying: 'applying',
            completed: 'completed',
            partly_applied: 'partlyApplied',
            failed: 'failed',
            canceled: 'canceled',
        }[set.status];

        return this.label(key)
            .replace('%count%', String(count))
            .replace('%done%', String(set.progress?.done ?? 0))
            .replace('%total%', String(set.progress?.total ?? 0))
            .replace('%page%', page);
    }

    private outcomeText(operation: PageChangeOperation): string {
        const edit = operation.op === 'update_item';
        const status = {
            applied: this.label(edit ? 'statusChanged' : 'statusApplied'),
            skipped: this.label('statusSkipped'),
            failed: this.label(edit ? 'statusNotChanged' : 'statusNotApplied'),
            blocked: this.label(edit ? 'statusNotChanged' : 'statusNotApplied'),
        }[operation.status] || '';
        const reason = operation.reason ? this.label(`reason_${operation.reason}`) : '';

        return [status, reason].filter(Boolean).join('. ');
    }

    private currentVersion(setId: string): string | null {
        const set = this.statusItems.filter(isPageChangeSetStatusItem).find(item => item.id === setId);
        return set ? versionOf(set) : null;
    }

    private label(key: string): string {
        return this.options.labels[key] || '';
    }
}

const versionOf = (set: PageChangeSetStatusItem): string => `${set.status}:${set.revision}`;

const pendingCount = (set: PageChangeSetStatusItem): number => (
    set.operations.filter(operation => operation.status === 'pending').length
);
